import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import { clsx } from "clsx";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Button from "components/Button";
import Input from "components/Input";
import { RefLoadingSpinner } from "components/LoadingSpinner";
import Select from "components/Select";
import { INVITED_CARRIER_SEARCH_FILTER_LIST } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import InvitedCarrierItem from "features/main/HaulPay/Shared/InvitedCarrierItem";
import useDeviceType from "hooks/useDeviceType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { IInvitedCarrier } from "models/HaulPay";
import { getInvitedCarriersAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";

export default function BrokerInvitedCarrier() {
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserDetails);
  const groups = userDetails?.groups ?? [];
  const { isDesktop } = useDeviceType();
  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState(INVITED_CARRIER_SEARCH_FILTER_LIST[0]);

  const fetchInvitedCarriers = async (params: any) => {
    return getInvitedCarriersAPI(params.group_id, params.limit, params.offset);
  };

  const {
    data: invitedCarriers,
    loading: isLoadingMore,
    observerRef,
    deleteItem
  } = useInfiniteScroll<IInvitedCarrier>(
    fetchInvitedCarriers,
    {
      threshold: 0.9,
      group_id: groups[0]?.id
    },
    [groups[0]?.id]
  );

  function goToAddNew() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const onInvitationDeleted = (id: string) => {
    deleteItem(id);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 desktop:gap-6">
        <div className="relative flex-1">
          <Input
            prefixNode={
              <Icon path={mdiMagnify} className={clsx(styles.searchButton, { "z-40": isOpen })} />
            }
            placeholder="Search for"
            inputClass={clsx(
              styles.searchWrapper,
              { "!bg-card": !isDesktop && !isOpen },
              { "z-30": isOpen }
            )}
            onFocus={() => setIsOpen(true)}
          />
          {isOpen && (
            <>
              <div className="fixed inset-0 z-10 bg-card/90" />
              <div className={styles.searchModal}>
                <Select
                  placeholder="Carrier Name"
                  options={INVITED_CARRIER_SEARCH_FILTER_LIST}
                  value={filterValue}
                  onChange={setFilterValue}
                  btnClass="!py-3 !pl-3 desktop:!pr-20 !rounded-lg text-sm h-11"
                  className="w-fit mobile:w-full"
                  arrowClass="!right-1"
                />
                <Button
                  variant="outline"
                  rounded
                  className="self-end h-8 text-xs w-fit"
                  onClick={() => setIsOpen(false)}>
                  Search
                </Button>
              </div>
            </>
          )}
        </div>
        <Button
          rounded
          className="!h-10 desktop:!h-12 mobile:text-xs desktop:!px-12 !px-9 desktop:!rounded-base"
          onClick={goToAddNew}>
          {isDesktop ? "Add New" : "ADD NEW"}
        </Button>
      </div>

      {invitedCarriers.map((item) => (
        <InvitedCarrierItem
          key={item.id}
          data={item}
          onInvitationDeleted={() => onInvitationDeleted(item.id)}
        />
      ))}
      <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
    </div>
  );
}
